import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {useEffect, useState} from "react";
import {FiChevronDown, FiChevronUp, FiTrash2} from "react-icons/fi";
import useApiRequest from "../../hooks/useApiRequest";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../ui/ConfirmDialog";
import {toLocalDate} from "../../utils/formatData";
import {snackbar} from "../../utils/snackbar";
import {DEFAULT_PAGINATION, DEFAULT_PAGE_SIZE_OPTIONS} from "../../utils/pagination";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../../hooks/useAuthPermissions";

const TokenRow = ({token, onDelete}) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  // El cliente OAuth puede venir embebido como token.client o token.oauth_client
  const oauthClient = token?.client ?? token?.oauth_client ?? null;
  const hasClient = !!oauthClient;

  return (
    <>
      <TableRow sx={{"&:hover": {backgroundColor: theme.palette.listView?.rowHoverBackground}}}>
        <TableCell sx={{width: 48}}>
          <IconButton
            size="small"
            onClick={() => setOpen((p) => !p)}
            disabled={!hasClient}
            aria-label={open ? "colapsar" : "expandir"}
          >
            {open ? <FiChevronUp /> : <FiChevronDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Tooltip title={token.token || ""}>
            <Typography
              variant="body2"
              sx={{maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
            >
              {token.token || "-"}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>{token.type || "-"}</TableCell>
        <TableCell>{toLocalDate(token.expires_at)}</TableCell>
        <TableCell>
          <IconButton
            size="small"
            color="error"
            aria-label="delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(token.id);
            }}
          >
            <FiTrash2 />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Fila expandible con OAuth client */}
      <TableRow>
        <TableCell colSpan={5} sx={{py: 0, border: open ? undefined : "none"}}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{pl: 6, py: 1.5}}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{mb: 1, display: "block"}}>
                OAuth Client
              </Typography>
              {hasClient ? (
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {oauthClient.client_id && (
                    <Chip label={`ID: ${oauthClient.client_id}`} size="small" variant="outlined" />
                  )}
                  {oauthClient.name && <Chip label={`Nombre: ${oauthClient.name}`} size="small" variant="outlined" />}
                  {oauthClient.allowed_grants && (
                    <Chip
                      label={`Grants: ${Array.isArray(oauthClient.allowed_grants) ? oauthClient.allowed_grants.join(", ") : oauthClient.allowed_grants}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {oauthClient.redirect_uris && (
                    <Chip
                      label={`Redirect URIs: ${Array.isArray(oauthClient.redirect_uris) ? oauthClient.redirect_uris.join(", ") : oauthClient.redirect_uris}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Sin información de cliente OAuth
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const UserTokensGrid = ({userId}) => {
  const theme = useTheme();
  const {can} = useAuthPermissions();
  const canReadTokens = can(ACTIONS.READ, RESOURCES.TOKENS);
  const {data, loading, list} = useApiRequest({baseUrl: `/tokens?userId=${userId}`, resource: "tokens"});
  const {remove} = useApiRequest({baseUrl: "/tokens", resource: "tokens"});

  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: DEFAULT_PAGINATION});

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const {success} = await remove(id, {silentError: true});
    if (!success) return;

    snackbar.success("Token eliminado correctamente.");
    await list({page: paginationModel.page, limit: paginationModel.pageSize}, {silentError: true});
  });

  useEffect(() => {
    if (!canReadTokens) return;
    list({page: paginationModel.page, limit: paginationModel.pageSize}, {silentError: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, canReadTokens]);

  if (!canReadTokens) {
    return (
      <Typography variant="body2" color="text.secondary">
        No tienes permisos para ver los tokens de este usuario.
      </Typography>
    );
  }

  const handleOpenDeleteDialog = (id) => {
    const tokenList = Array.isArray(data?.data) ? data.data : [];
    const found = tokenList.find((token) => token.id === id);
    openDialog(id, found?.token || "");
  };

  const tokens = Array.isArray(data?.data) ? data.data : [];
  const totalItems = data?.pagination?.totalItems ?? tokens.length;

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.card?.border}`,
          borderRadius: theme.palette.card?.borderRadius,
          bgcolor: theme.palette.card?.background,
          boxShadow: theme.palette.card?.borderShadow
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{backgroundColor: theme.palette.listView?.headerBackground}}>
                <TableCell sx={{width: 48}} />
                <TableCell sx={{color: theme.palette.listView?.headerText, fontWeight: "bold"}}>Token</TableCell>
                <TableCell sx={{color: theme.palette.listView?.headerText, fontWeight: "bold"}}>Tipo</TableCell>
                <TableCell sx={{color: theme.palette.listView?.headerText, fontWeight: "bold"}}>Expiración</TableCell>
                <TableCell sx={{width: 60}} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{py: 3}}>
                    <Typography variant="body2" color="text.secondary">
                      Cargando tokens…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : tokens.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{py: 3}}>
                    <Typography variant="body2" color="text.secondary">
                      Sin tokens
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tokens.map((token) => <TokenRow key={token.id} token={token} onDelete={handleOpenDeleteDialog} />)
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={DEFAULT_PAGE_SIZE_OPTIONS}
                  count={totalItems}
                  rowsPerPage={paginationModel.pageSize}
                  page={paginationModel.page}
                  onPageChange={(_, newPage) => setPaginationModel((p) => ({...p, page: newPage}))}
                  onRowsPerPageChange={(e) => setPaginationModel({page: 0, pageSize: parseInt(e.target.value, 10)})}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({from, to, count}) =>
                    `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="token"
        entityLabel={itemName}
      />
    </>
  );
};

export default UserTokensGrid;
